import React, { useState, useEffect } from "react";
import { Card, Table, Button, DatePicker, Space, message, Badge, Row, Col } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import ReportService from "../../services/ReportService";

const { RangePicker } = DatePicker;

const STATUS_COLUMNS = [
    { key: "status11", title: "11", label: "Gönderilmeye\nHazır", color: "#4096ff" },
    { key: "status3", title: "3", label: "Teklif\nGönderildi", color: "#1677ff" },
    { key: "status12", title: "12", label: "Manuel\nTakip", color: "#4096ff" },
    { key: "status14", title: "14", label: "Sözleşme\nReddedildi", color: "orange" },
    { key: "status10", title: "10", label: "Sözleşme\nYüklendi", color: "#73d13d" },
    { key: "status6", title: "6", label: "Planlama", color: "#73d13d" },
    { key: "status7", title: "7", label: "Yasal Bildirim\n(İSG-Katip)", color: "#73d13d" },
    { key: "status5", title: "5", label: "Kontrol ve\nRaporlama", color: "#73d13d" },
    { key: "status4", title: "4", label: "Kontrol\nTamamlandı", color: "#73d13d" },
    { key: "status2", title: "2", label: "Olumsuz", color: "#ff4d4f" },
    { key: "status13", title: "13", label: "Teklif İptal\nEdildi", color: "#ff4d4f" }
];

type ProposalStatusCounts = {
    firstName: string;
    lastName: string;
    email: string;
    accountId: number;
    total: number;
    status2: number;
    status3: number;
    status4: number;
    status5: number;
    status6: number;
    status7: number;
    status10: number;
    status11: number;
    status12: number;
    status13: number;
    status14: number;
};

type ProposalStatusCountsTotal = {
    total: number;
    status2: number;
    status3: number;
    status4: number;
    status5: number;
    status6: number;
    status7: number;
    status10: number;
    status11: number;
    status12: number;
    status13: number;
    status14: number;
};

const columns: ColumnsType<ProposalStatusCounts> = [
    {
        title: "Ad Soyad / E-posta",
        render: (_, r) => (
            <>
                {r.firstName} {r.lastName}
                <br />
                <span style={{ color: "#888", fontSize: 12 }}>{r.email}</span>
            </>
        ),
        key: "adsoyad",
        width: 200,
        fixed: "left"
    },
    ...STATUS_COLUMNS.map(col => ({
        title: (
            <div style={{ textAlign: "center" }}>
                <span style={{ fontWeight: 600 }}>{col.label}</span>
            </div>
        ),
        dataIndex: col.key,
        key: col.key,
        align: "center" as "center",
        width: 100,
        render: (value: number) =>
            value > 0
                ? <b>{value}</b>
                : <span style={{ color: "#aaa" }}>{value}</span>
    })),
    {
        title: <b>Toplam</b>,
        dataIndex: "total",
        key: "total",
        align: "center" as "center",
        width: 80,
        render: (value: number) =>
            value > 0
                ? <b>{value}</b>
                : <span style={{ color: "#aaa" }}>{value}</span>
    }
];

const ProposalStatusCountsDashboard: React.FC = () => {
    const [range, setRange] = useState<[Dayjs, Dayjs]>([
        dayjs().startOf("month"),
        dayjs()
    ]);
    const [tableData, setTableData] = useState<ProposalStatusCounts[]>([]);
    const [total, setTotal] = useState<ProposalStatusCountsTotal | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchAll = async () => {
        const [startDate, endDate] = range;
        if (!startDate || !endDate) {
            message.warning("Lütfen tarih aralığı seçiniz.");
            return;
        }

        setLoading(true);
        try {
            const apiStart = startDate.format("YYYY-MM-DD");
            const apiEnd = endDate.format("YYYY-MM-DD");
            const [tableRes, totalRes] = await Promise.all([
                ReportService.getProposalStatusCounts(apiStart, apiEnd),
                ReportService.getProposalStatusCountsTotal(apiStart, apiEnd)
            ]);
            setTableData(tableRes.data);
            setTotal(totalRes.data);
        } catch (err: any) {
            setTableData([]);
            setTotal(null);
            message.error(
                err?.response?.data?.message ||
                err?.message ||
                "Rapor alınırken hata oluştu!"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <Card
                title="Teklif Durum / İş-Ortağı Raporu"
                extra={
                    <Space>
                        <RangePicker
                            value={range}
                            onChange={values => setRange(values as [Dayjs, Dayjs])}
                            format="DD/MM/YYYY"
                            placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
                            allowClear={false}
                        />
                        <Button type="primary" onClick={fetchAll} loading={loading}>
                            Raporu Getir
                        </Button>
                    </Space>
                }
                style={{ marginBottom: 24 }}
            >
                {total && (
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }} justify="space-between">
                        {STATUS_COLUMNS.map(col => (
                            <Col>
                                <Card
                                    bordered={false}
                                    key={col.key}
                                    style={{
                                        background: "#f0f2f5",
                                        textAlign: "center",
                                        minHeight: 140,
                                        width: "100%",
                                        minWidth: 120
                                    }}>

                                    <div style={{ fontSize: 14, color: "#555", minHeight: 60 }}>
                                        {col.label.split("\n").map((line, i) => (
                                            <b key={i}>
                                                {i > 0 && <br />}
                                                {line}
                                            </b>
                                        ))}
                                    </div>
                                    <div style={{ color: col.color, fontWeight: 700, fontSize: 28 }}>
                                        {(total as any)[col.key]}
                                    </div>
                                </Card>
                            </Col>
                        ))}
                        <Col>
                            <Card
                                bordered={false}
                                style={{
                                    background: "#e6f4ff",
                                    textAlign: "center",
                                    width: 110,
                                    border: "1.5px solid #1764b2"
                                }}
                            >
                                <div style={{ fontSize: 14, color: "#1764b2", minHeight: 60 }}>Toplam</div>
                                <div style={{ fontWeight: 900, fontSize: 28, color: "#1764b2" }}>
                                    {total.total}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                )}
                <Table
                    columns={columns}
                    dataSource={tableData}
                    loading={loading}
                    rowKey="accountId"
                    scroll={{ x: "max-content" }}
                    locale={{
                        emptyText: "Veri bulunamadı. Lütfen tarih aralığı seçip sorgulayınız."
                    }}
                    bordered
                    size="small"
                    pagination={false}
                />
            </Card>
        </div>
    );
};

export default ProposalStatusCountsDashboard;
